import cn from 'classnames';
import { FC, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router';

import type { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import { templatesApi } from '@/shared/api/transcriptor/templatesApi';
import { useAppSelector } from '@/shared/hooks';
import { Button, Tabs, toastCaller } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import {
  filterCatalogItems,
  getPartnerTemplateName,
  isBlockNoteTemplate,
  isClinicOwnTemplate,
  resolveCatalogFilter,
  TEMPLATE_EDIT_SEARCH_PARAM,
  TEMPLATE_SERIES_SEARCH_PARAM,
  TemplateCatalogFilters,
  templateDepartmentMessages,
  templateGroupMessages,
  type CatalogFilter,
  type DocumentTemplate,
} from '@/entities/templates';
import { normalizeContentLanguage } from '@/entities/transcriptor';

import { useLocalizedTemplates } from '@/features/templateList';

import {
  useLibraryTemplates,
  useNoteLists,
  useRefreshTemplateList,
  useTemplateEdit,
  useUnsavedChangesGuard,
  useWorkspaceCatalog,
  type TemplateEdit,
} from '../hooks';
import {
  buildConsultationLibrary,
  buildPartnerLibrary,
  buildProtocolLibrary,
  buildRegistryLibrary,
  isFieldListTemplate,
  prepareBlockSummary,
  withFieldListKeys,
  type WorkspaceCatalogItem,
} from '../lib';

import { CatalogRetry } from './CatalogRetry';
import { CommunityVersionOffer } from './CommunityVersionOffer';
import { DocumentOffPanel } from './DocumentOffPanel';
import { InlineConfirm } from './InlineConfirm';
import { LibraryTemplate } from './LibraryTemplate';
import { NoteDocument } from './NoteDocument';
import styles from './TemplateLibraryView.module.scss';
import { TemplateLoader, TemplatePreviewContent } from './TemplatePreview';
import { TemplateSaveBar } from './TemplateSaveBar';
import {
  CatalogOriginLabel,
  WorkspaceTemplateCatalog,
} from './WorkspaceTemplateCatalog';

type Props = {
  // Without it the templates are only viewed: scribe refuses their writes.
  canEdit: boolean;
  className?: string;
};

type TemplatesTab = 'mine' | 'library' | 'partner';

const TEMPLATES_TABS: TemplatesTab[] = ['mine', 'library', 'partner'];

type ListedTemplateEdit = Pick<TemplateEdit, 'source' | 'cancel' | 'restart'>;

type BlockTemplateProps = {
  template: DocumentTemplate;
  // Without it the template is only read.
  edit?: TemplateEdit;
  canEdit: boolean;
  sourceLabel: ReactNode;
  readOnlyNotice: ReactNode;
};

// A block template on the note's own surface, without a note: "Edit
// template" opens it as one document, saved as the clinic's next version.
const BlockTemplate: FC<BlockTemplateProps> = ({
  template,
  edit,
  canEdit,
  sourceLabel,
  readOnlyNotice,
}) => {
  const { formatMessage } = useIntl();
  const { lists, failed } = useNoteLists(template);
  const partner = template.partner_source?.partner;
  const draft = edit?.draft;

  return (
    <NoteDocument
      template={edit?.changed && draft ? draft.template : template}
      sourceLabel={sourceLabel}
      notice={
        partner !== undefined ? (
          <FormattedMessage
            id="workspace.templates.partnerReadOnly"
            defaultMessage="This template follows a {partner} template and cannot be edited here: its structure comes from the partner's template."
            values={{ partner: getPartnerTemplateName(formatMessage, partner) }}
          />
        ) : (
          readOnlyNotice
        )
      }
      view={{ lists, listsFailed: failed }}
      edit={canEdit ? edit : undefined}
      saveBar={
        edit &&
        draft && (
          <TemplateSaveBar
            draft={draft}
            source={edit.source ?? template}
            blockingIssues={edit.documentIssues}
            onSaved={edit.complete}
            onDiscard={edit.cancel}
            onResume={edit.start}
            onRefused={edit.reportIssues}
            onShowIssue={edit.show}
            onReloaded={edit.restart}
          />
        )
      }
    />
  );
};

// "My templates" holds the protocol templates and the consultation summaries
// and evaluations the clinic edited, added or made itself, each shown on the
// surface the recording page shows it on and edited there as a template:
// block notes and field lists are saved as the clinic's next version. Templates with options are
// only read here. "Library" shows the shared templates read-only; one added
// from there becomes the clinic's copy and opens under "My templates".
// "Partner", there only while the clinic has some, shows the templates from
// partners' systems read-only.
// Recording no longer starts here. The address names the open template's
// series (`?series=`), so a link to another one, also while the page is
// open, opens it as a choice in the list does.
export const TemplateLibraryView: FC<Props> = ({ canEdit, className }) => {
  const { formatMessage } = useIntl();
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<TemplatesTab>('mine');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Another template chosen while the shown one holds unsaved changes.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [libraryId, setLibraryId] = useState<string | null>(null);
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // The clinic's copy of a library template, opened once the list names it.
  const [copyId, setCopyId] = useState<string | null>(null);
  const [viewedCommunityId, setViewedCommunityId] = useState<string | null>(
    null,
  );
  const { templateVersions, templatesLoading, loadError, retry } =
    useWorkspaceCatalog();
  const localized = useLocalizedTemplates(true);
  const templates = localized.documentTemplates;
  const language = localized.contentLanguage;
  const protocolsGroup = formatMessage({
    id: 'workspace.templates.protocolsGroup',
    defaultMessage: 'Treatment protocols',
  });
  const items = [
    ...buildProtocolLibrary({ templates: localized.documentTemplates }).map(
      (item) => ({
        ...item,
        group: protocolsGroup,
      }),
    ),
    ...buildConsultationLibrary({
      schemas: localized.schemas,
      templates: localized.documentTemplates,
    }),
  ];
  const ownTemplates = localized.documentTemplates.filter(isClinicOwnTemplate);
  const isOwn = (item: WorkspaceCatalogItem) =>
    ownTemplates.some((template) => template.series_id === item.id);
  const findListed = (seriesId: string | null) =>
    templates.find((template) => template.series_id === seriesId);
  const partnerItems = buildPartnerLibrary({
    items,
    partnerName: (partner) => getPartnerTemplateName(formatMessage, partner),
  });
  const mineItems = items
    .filter((item) => item.partner === undefined && isOwn(item))
    .map((item) => ({
      ...item,
      communityVersionId: findListed(item.id)?.newer_community_template_id,
    }));
  const shownTab = tab === 'partner' && !partnerItems.length ? 'mine' : tab;
  const partnerSelected = partnerItems.find((item) => item.id === partnerId);
  const partnerVersionId = templates.find(
    (template) => template.series_id === partnerSelected?.id,
  )?.id;
  const partnerVersion = partnerVersionId
    ? templateVersions[partnerVersionId]
    : undefined;
  const linkedSeriesId =
    searchParams.get(TEMPLATE_SERIES_SEARCH_PARAM) ?? undefined;
  const linkedListed =
    !!linkedSeriesId && items.some((item) => item.id === linkedSeriesId);
  const linkedEdit = searchParams.get(TEMPLATE_EDIT_SEARCH_PARAM) === '1';
  const selected = items.find((item) => item.id === selectedId);
  const pending = items.find((item) => item.id === pendingId);
  const listed = findListed(selectedId);
  const versionId = listed?.id;
  const storedVersion = versionId ? templateVersions[versionId] : undefined;
  const version = storedVersion
    ? prepareBlockSummary(storedVersion)
    : undefined;
  const blockNote = !!version && isBlockNoteTemplate(version);
  const fieldList = !!version && isFieldListTemplate(version);
  // Block notes and the fields of a summary or an evaluation are edited in
  // the one template editor; templates with options are only read here.
  const templateEdit = useTemplateEdit({
    source: blockNote
      ? version
      : fieldList
        ? withFieldListKeys(version)
        : undefined,
  });
  const refreshList = useRefreshTemplateList();
  // Only a template's structure is edited here, so only that can be lost.
  const unsavedName = templateEdit.changed ? version?.name : undefined;
  const unsaved = unsavedName !== undefined;
  const leaving = useUnsavedChangesGuard(unsaved);
  const library = useLibraryTemplates(tab === 'library');
  const [libraryFilter, setLibraryFilter] = useState<CatalogFilter>({
    source: 'ours',
    department: null,
    group: null,
  });
  const libraryItems = buildRegistryLibrary({
    assets: library.assets.filter(
      (asset) => normalizeContentLanguage(asset.language) === language,
    ),
    visitLabel: (group) => formatMessage(templateGroupMessages[group]),
    departmentLabel: (department) =>
      formatMessage(templateDepartmentMessages[department]),
  });
  const libraryAsset = library.assets.find((asset) => asset.id === libraryId);
  const copySeriesId = templates.find(
    (template) => template.id === copyId,
  )?.series_id;
  const readOnlyNotice = !canEdit && (
    <FormattedMessage
      id="workspace.templates.readOnly"
      defaultMessage="Only users with access to the clinic settings can edit and save clinic templates."
    />
  );

  // The router state keeps the way back.
  const showInAddress = (seriesId: string | null) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (seriesId) {
          next.set(TEMPLATE_SERIES_SEARCH_PARAM, seriesId);
        } else {
          next.delete(TEMPLATE_SERIES_SEARCH_PARAM);
        }
        return next;
      },
      { replace: true, state: location.state },
    );

  const open = (seriesId: string) => {
    setSelectedId(seriesId);
    setPendingId(null);
    showInAddress(seriesId);
  };

  const select = (seriesId: string) => {
    if (partnerItems.some((item) => item.id === seriesId)) {
      setTab('partner');
      setPartnerId(seriesId);
      return;
    }
    setTab('mine');
    if (seriesId === selectedId) {
      return;
    }
    if (unsaved) {
      setPendingId(seriesId);
      return;
    }
    open(seriesId);
  };

  const keepSelected = () => {
    setPendingId(null);
    showInAddress(selectedId);
  };

  useEffect(() => {
    if (linkedListed && linkedSeriesId) {
      select(linkedSeriesId);
    }
    // A link is followed once it changes or its series is listed, against the
    // selection and unsaved changes of that render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedSeriesId, linkedListed]);

  // A document's "Edit template" opens its series in the edit mode, once the
  // series' version is shown; the address then names the series only.
  useEffect(() => {
    if (!linkedEdit || !version || version.series_id !== linkedSeriesId) {
      return;
    }
    if (blockNote && canEdit && version.partner_source?.partner === undefined) {
      templateEdit.start();
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(TEMPLATE_EDIT_SEARCH_PARAM);
        return next;
      },
      { replace: true, state: location.state },
    );
    // The edit starts once, when the linked series' version is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedEdit, version?.id]);

  useEffect(() => {
    if (!copySeriesId) {
      return;
    }
    setCopyId(null);
    if (items.some((item) => item.id === copySeriesId)) {
      select(copySeriesId);
    } else {
      setTab('mine');
    }
    // The copy is opened once, against the selection and unsaved changes of
    // the render the list names it in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copySeriesId]);

  // An edit started again on the series' current version is made on a
  // version the list may not name yet: the list is read again, so the page
  // shows that version.
  const readListAgainFor = (sourceIds: (string | undefined)[]) => {
    const unlisted = sourceIds.some(
      (sourceId) =>
        !!sourceId && !templates.some((template) => template.id === sourceId),
    );
    if (unlisted) {
      void refreshList();
    }
  };

  const followList = <T extends ListedTemplateEdit>(edit: T): T => ({
    ...edit,
    cancel: () => {
      readListAgainFor([edit.source?.id]);
      edit.cancel();
    },
    restart: (current: DocumentTemplate) => {
      edit.restart(current);
      readListAgainFor([current.id]);
    },
  });

  const dropChangesAndOpen = (seriesId: string) => {
    readListAgainFor([templateEdit.source?.id]);
    templateEdit.cancel();
    open(seriesId);
  };

  const addToMine = async (asset: RegistryAsset) => {
    setAdding(true);
    try {
      setCopyId(await library.adopt(asset));
      toastCaller({
        type: 'success',
        message: formatMessage({
          id: 'communityCatalog.addToOrgSuccess',
          defaultMessage: 'Template added to your organization',
        }),
      });
      library.reload();
      await refreshList();
    } catch {
      toastCaller({
        type: 'error',
        message: formatMessage({
          id: 'communityCatalog.addToOrgError',
          defaultMessage: 'Failed to add template to organization',
        }),
      });
    } finally {
      setAdding(false);
    }
  };

  const copyClassicSummary = async (source: DocumentTemplate) => {
    setAdding(true);
    try {
      const result = await templatesApi.cloneDocumentTemplate(source.id, {
        target_organization_id: organizationId,
      });
      setCopyId(result.local_asset_id);
      await refreshList();
    } catch {
      toastCaller({
        type: 'error',
        message: formatMessage({
          id: 'communityCatalog.addToOrgError',
          defaultMessage: 'Failed to add template to organization',
        }),
      });
    } finally {
      setAdding(false);
    }
  };

  const renderLibraryTemplate = () => {
    if (!libraryAsset) {
      return (
        <DocumentOffPanel
          title={
            <FormattedMessage
              id="workspace.templates.chooseLibrary"
              defaultMessage="Choose a library template"
            />
          }
        >
          <p className="p3">
            <FormattedMessage
              id="workspace.templates.chooseLibraryHint"
              defaultMessage="Save a library template to the clinic to return to it and edit it."
            />
          </p>
        </DocumentOffPanel>
      );
    }
    const { localCloneId } = libraryAsset;
    return (
      <>
        {readOnlyNotice && (
          <p className={cn('p4', styles.notice)}>{readOnlyNotice}</p>
        )}
        <LibraryTemplate
          key={libraryAsset.id}
          asset={libraryAsset}
          canAdd={canEdit}
          adding={adding}
          saved={ownTemplates.some((template) => template.id === localCloneId)}
          onAdd={() => addToMine(libraryAsset)}
          onOpen={() => setCopyId(localCloneId)}
        />
      </>
    );
  };

  const renderPartnerTemplate = () => {
    if (!partnerSelected || !partnerVersionId) {
      return (
        <DocumentOffPanel
          title={
            <FormattedMessage
              id="workspace.templates.choosePartner"
              defaultMessage="Choose a partner template"
            />
          }
        >
          <p className="p3">
            <FormattedMessage
              id="workspace.templates.choosePartnerHint"
              defaultMessage="Partner templates are only shown here: their structure comes from the partner's system."
            />
          </p>
        </DocumentOffPanel>
      );
    }
    if (!partnerVersion) {
      return (
        <TemplateLoader key={partnerVersionId} templateId={partnerVersionId} />
      );
    }
    return isBlockNoteTemplate(partnerVersion) ? (
      <BlockTemplate
        key={partnerVersion.id}
        template={partnerVersion}
        canEdit={false}
        sourceLabel={<CatalogOriginLabel origin={partnerSelected.origin} />}
        readOnlyNotice={null}
      />
    ) : (
      <TemplatePreviewContent
        key={partnerVersion.id}
        template={partnerVersion}
      />
    );
  };

  const renderTemplate = () => {
    if (!selected || !versionId) {
      return (
        <DocumentOffPanel
          title={
            <FormattedMessage
              id="workspace.templates.choose"
              defaultMessage="Choose a template to edit"
            />
          }
        >
          <p className="p3">
            <FormattedMessage
              id="workspace.templates.chooseHint"
              defaultMessage="Changes are saved as a new version of the clinic's template; encounters started later use it."
            />
          </p>
        </DocumentOffPanel>
      );
    }
    if (!version) {
      return <TemplateLoader key={versionId} templateId={versionId} />;
    }
    const sourceLabel = <CatalogOriginLabel origin={selected.origin} />;
    if (blockNote) {
      return (
        <BlockTemplate
          key={version.id}
          template={version}
          edit={followList(templateEdit)}
          canEdit={canEdit}
          sourceLabel={sourceLabel}
          readOnlyNotice={readOnlyNotice}
        />
      );
    }
    if (fieldList) {
      // The current version a refused save reloads is keyed as the shown one.
      const edit = followList({
        ...templateEdit,
        restart: (current: DocumentTemplate) =>
          templateEdit.restart(withFieldListKeys(current)),
      });
      return (
        <>
          {readOnlyNotice && (
            <p className={cn('p4', styles.notice)}>{readOnlyNotice}</p>
          )}
          {canEdit &&
            version.type === 'SUMMARY' &&
            !version.transcriptor_v2 && (
              <Button
                loading={adding}
                disabled={adding || unsaved}
                onClick={() => void copyClassicSummary(version)}
              >
                <FormattedMessage
                  id="workspace.summary.copyForTools"
                  defaultMessage="Create a copy with the full editor"
                />
              </Button>
            )}
          <TemplatePreviewContent
            key={version.id}
            template={version}
            fieldList={{
              edit: canEdit ? edit : undefined,
              sourceLabel,
              saveBar: edit.draft && (
                <TemplateSaveBar
                  draft={edit.draft}
                  source={edit.source ?? version}
                  blockingIssues={edit.documentIssues}
                  onSaved={edit.complete}
                  onDiscard={edit.cancel}
                  onResume={edit.start}
                  onRefused={edit.reportIssues}
                  onShowIssue={edit.show}
                  onReloaded={edit.restart}
                />
              ),
            }}
          />
        </>
      );
    }
    return (
      <>
        <p
          className={cn('p4', styles.notice)}
          data-component="workspace-templates-options-read-only"
        >
          <FormattedMessage
            id="workspace.templates.optionsReadOnly"
            defaultMessage="Templates with options are edited in the clinic settings, under Templates; here they are only shown."
          />
        </p>
        <TemplatePreviewContent key={version.id} template={version} />
      </>
    );
  };

  // An edit in progress is saved or dropped before the community version is
  // taken over it.
  const communityVersionId = templateEdit.editing
    ? undefined
    : listed?.newer_community_template_id;

  const renderEditor = () => {
    if (!selected || !versionId || !communityVersionId) {
      return renderTemplate();
    }
    const viewing = viewedCommunityId === communityVersionId;
    return (
      <>
        <CommunityVersionOffer
          key={communityVersionId}
          templateId={versionId}
          communityTemplateId={communityVersionId}
          canEdit={canEdit}
          viewing={viewing}
          onViewingChange={(shown) =>
            setViewedCommunityId(shown ? communityVersionId : null)
          }
        />
        {!viewing && renderTemplate()}
      </>
    );
  };

  return (
    <div
      className={cn(styles.page, className)}
      data-section="transcriptor-templates"
    >
      {loadError && <CatalogRetry onRetry={retry} />}
      {leaving.state === 'blocked' && (
        <InlineConfirm
          message={
            <FormattedMessage
              id="workspace.templates.leaveWarning"
              defaultMessage="“{name}” has unsaved changes. Leave the page and drop them?"
              values={{ name: unsavedName }}
            />
          }
          confirmLabel={
            <FormattedMessage
              id="workspace.templates.replace"
              defaultMessage="Drop the changes"
            />
          }
          onConfirm={() => leaving.proceed()}
          onCancel={() => leaving.reset()}
        />
      )}
      <Tabs.Root
        className={styles.tabs}
        value={shownTab}
        onValueChange={(value) =>
          setTab(
            TEMPLATES_TABS.find((candidate) => candidate === value) ?? 'mine',
          )
        }
      >
        <Tabs.List size="small">
          <Tabs.Trigger value="mine" size="small" className={styles.tab}>
            <FormattedMessage
              id="workspace.templates.mineTab"
              defaultMessage="Saved by clinic"
            />
          </Tabs.Trigger>
          <Tabs.Trigger value="library" size="small" className={styles.tab}>
            <FormattedMessage
              id="workspace.templates.libraryTab"
              defaultMessage="Library"
            />
          </Tabs.Trigger>
          {partnerItems.length > 0 && (
            <Tabs.Trigger value="partner" size="small" className={styles.tab}>
              <FormattedMessage
                id="workspace.templates.partnerTab"
                defaultMessage="Partner"
              />
            </Tabs.Trigger>
          )}
        </Tabs.List>
        {/* Kept while the library is shown, so an open edit keeps its
            document and undo history. */}
        <Tabs.Content value="mine" forceMount className={styles.layout}>
          <aside className={cn(styles.card, styles.catalogCard)}>
            <WorkspaceTemplateCatalog
              items={mineItems}
              selectedIds={selected ? [selected.id] : []}
              disabled={false}
              heading={false}
              fill
              collapsibleGroups
              emptyText={
                <FormattedMessage
                  id="workspace.templates.mineEmpty"
                  defaultMessage="Templates you save from the library or edit appear here."
                />
              }
              status={
                loadError ? 'failed' : templatesLoading ? 'loading' : undefined
              }
              onSelect={select}
            >
              {pending && (
                <InlineConfirm
                  message={
                    <FormattedMessage
                      id="workspace.templates.replaceWarning"
                      defaultMessage="“{name}” has unsaved changes. Open “{next}” and drop them?"
                      values={{ name: unsavedName, next: pending.name }}
                    />
                  }
                  confirmLabel={
                    <FormattedMessage
                      id="workspace.templates.replace"
                      defaultMessage="Drop the changes"
                    />
                  }
                  onConfirm={() => dropChangesAndOpen(pending.id)}
                  onCancel={keepSelected}
                />
              )}
            </WorkspaceTemplateCatalog>
          </aside>
          <main className={styles.card}>{renderEditor()}</main>
        </Tabs.Content>
        <Tabs.Content value="library" className={styles.layout}>
          <aside className={cn(styles.card, styles.catalogCard)}>
            <WorkspaceTemplateCatalog
              fill
              items={filterCatalogItems(
                libraryItems,
                resolveCatalogFilter(libraryItems, libraryFilter),
              )}
              selectedIds={libraryAsset ? [libraryAsset.id] : []}
              disabled={false}
              heading={false}
              collapsibleGroups
              status={
                library.failed
                  ? 'failed'
                  : library.loading
                    ? 'loading'
                    : undefined
              }
              onSelect={setLibraryId}
            >
              <TemplateCatalogFilters
                items={libraryItems}
                filter={libraryFilter}
                groupChips={false}
                compact
                onChange={setLibraryFilter}
              />
              {library.failed && <CatalogRetry onRetry={library.reload} />}
            </WorkspaceTemplateCatalog>
          </aside>
          <main className={styles.card}>{renderLibraryTemplate()}</main>
        </Tabs.Content>
        {partnerItems.length > 0 && (
          <Tabs.Content value="partner" className={styles.layout}>
            <aside className={cn(styles.card, styles.catalogCard)}>
              <WorkspaceTemplateCatalog
                fill
                items={partnerItems}
                selectedIds={partnerSelected ? [partnerSelected.id] : []}
                disabled={false}
                heading={false}
                collapsibleGroups
                onSelect={setPartnerId}
              />
            </aside>
            <main className={styles.card}>{renderPartnerTemplate()}</main>
          </Tabs.Content>
        )}
      </Tabs.Root>
    </div>
  );
};
